import {ChangeDetectionStrategy, Component, computed, effect, Injectable, input, linkedSignal, Signal} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {AvstemningId, AvstemningMedResultatModell, AvstemningsalternativId, AvstemningsalternativMedResultatModell, AvstemningsstatusModell, DokumentId, EntitetType, TilgangsrolleMedlem} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {HtmlFormatertKomponent} from "web-ng2/delt/komponenter/html-formatert/html-formatert";
import {C3TodeltPaiKomponent, C3TodeltPaiKonfig} from "web-ng2/delt/komponenter/c3-todelt-pai/c3-todelt-pai";
import {TranslateModule} from "@ngx-translate/core";
import {i18n, I18nTekst, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {MatIcon} from "@angular/material/icon";
import {første} from "felles-ng2/støttefunksjoner/lister";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {FormControl, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MatDialog} from "@angular/material/dialog";
import {toSignal} from "@angular/core/rxjs-interop";
import {map} from "rxjs/operators";
import {RedigerAvstemningDialogKomponent} from "web-ng2/moduler/avstemning/modale/rediger-avstemning-dialog/rediger-avstemning-dialog";
import {AvstemningDatatjeneste} from "felles-ng2/tjenester/datalager/AvstemningDatatjeneste";
import {MeldingTilBrukerNg2} from "felles-ng2/tjenester/MeldingTilBrukerNg2";
import {Dialog} from "felles-ng2/tjenester/Dialog";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatDivider} from "@angular/material/list";
import {MatMenu, MatMenuItem, MatMenuTrigger} from "@angular/material/menu";
import {MatRadioButton, MatRadioGroup} from "@angular/material/radio";
import {BekreftKlikkDirektiv} from "web-ng2/delt/direktiver/button--bekreftKlikk/button--bekreftKlikk";
import {InlineMeldingKomponent} from "web-ng2/delt/komponenter/inline-melding/inline-melding";
import {EntitetikonKomponent} from "web-ng2/delt/komponenter/entitetikon/entitetikon";
import {asynkArbeidPågårSignal} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {farge_grå_400_skagastølstind, farge_profil_500_bergen, farge_suksess_200_hoved} from "felles-ng2/fargepalett";
import {MatTooltip} from "@angular/material/tooltip";
import {BeslutningOpprettDialogKomponent} from "web-ng2/moduler/beslutning/modale/beslutning-opprett-dialog/beslutning-opprett-dialog";

const debug = initDebugger('AvstemningListevisningKomponent');

/**
 * Viser avstemning for bruk i listevisninger.
 */
@Component({
    selector: 'avstemning-listevisning',
    templateUrl: 'avstemning-listevisning.html',
    styleUrl: 'avstemning-listevisning.scss',
    host: {
        'data-testid': 'avstemning',
        '[attr.id]': "`avst-${avstemning().id}`"
    },
    imports: [
        TranslateModule,
        C3TodeltPaiKomponent,
        HtmlFormatertKomponent,
        MatIcon,
        FormsModule,
        MatButton,
        MatDivider,
        MatIconButton,
        MatMenu,
        MatMenuItem,
        MatRadioButton,
        MatRadioGroup,
        ReactiveFormsModule,
        MatMenuTrigger,
        BekreftKlikkDirektiv,
        InlineMeldingKomponent,
        EntitetikonKomponent,
        MatTooltip
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AvstemningListevisningKomponent {

    /** css selector uttrykk som kan benyttes for å lokalisere elmenet som kan benyttes i 'scroll til' uttrykk. */
    static scrollTilElementCssUttrykk(avstemningId: AvstemningId): string {
        return `#avst-${avstemningId}`;
    }

    avstemning = input.required<AvstemningMedResultatModell>();
    avstemningsstatus = input.required<AvstemningsstatusModell>();
    /** Dersom sann kan ikke avstemning redigeres */
    moeteLaastForRedigering = input.required<boolean>();

    valgtAlternativ = new FormControl<AvstemningsalternativMedResultatModell | null>(null);
    initieltValgtAlternativ = linkedSignal<AvstemningsalternativMedResultatModell | null>(() => this.avstemning().avstemningsalternativer.find(alternativ => alternativ.erDeltakersValg) ?? null);

    medlemHarSkriveRolle = computed(() => this.kontekst.autentisertBruker().inneharTilgangsrolleIGruppe(TilgangsrolleMedlem.skrive, this.avstemning().eiendeGruppeId));
    visAntallStemmerAvgitt = computed(() => this.medlemHarSkriveRolle() && erIkkeNil(this.avstemningsstatus()));
    /** Kun vis resultater dersom avstemning er lukket og minst én stemme er avgitt */
    erVisStemmeresultat = computed(() =>
      /*(this.kontekst.autentisertBruker().inneharTilgangsrolleIGruppe(TilgangsrolleMedlem.tilgangIkkePublisert, this.avstemning().eiendeGruppeId) ||*/ //<-- denne var med før - men avslører stemmegivning underveis som vel ikke er ok..
      !this.avstemning().erAapenForAvstemning &&
      erIkkeNil(første(this.avstemning().avstemningsalternativer)?.antallStemmer)
    );
    erAvstemningGjennomfoert = computed(() => this.avstemning().avstemningsalternativer.some(alternativ => alternativ.antallStemmer > 0));
    brukerKanStemme = computed(() => this.avstemning().erAapenForAvstemning && this.avstemning().deltakerErStemmeberettiget);
    harAvgittStemme = computed(() => this.brukerKanStemme() && erIkkeNil(this.initieltValgtAlternativ()));

    avstemningKanRedigeres = computed(() =>
      !this.moeteLaastForRedigering() &&
      this.medlemHarSkriveRolle() &&
      !this.avstemning().erAapenForAvstemning &&
      !this.avstemning().avstemningsalternativer.some(alternativ => (alternativ.antallStemmer ?? 0) > 0)
    );
    erValgEndret: Signal<boolean>;

    pieKonfig: Partial<C3TodeltPaiKonfig> = {
        ikkePåbegyntPieBorderFarge: farge_grå_400_skagastølstind,
        ikkePåbegyntFarge: '#ffffff00', // transparent farge gir riktig hover-effekt inni md-button
        andelFarge: farge_profil_500_bergen,
        fullførtFarge: farge_suksess_200_hoved
    }

    arbeider = asynkArbeidPågårSignal();
    readonly avstemningEntitetType = EntitetType.avstemning

    constructor(
      private avhengigheter: AvstemningListevisningKomponentAvhengigheter,
      private meldingTilBruker: MeldingTilBrukerNg2,
      private oversettelser: Oversettelser,
      private kontekst: BrukerkontekstTilbyder,
      private dialog: Dialog,
      private matDialog: MatDialog
    ) {
        effect(() => this.valgtAlternativ.setValue(this.avstemning().avstemningsalternativer.find(alternativ => alternativ.erDeltakersValg) ?? null));
        this.erValgEndret = toSignal(this.valgtAlternativ.valueChanges.pipe(map((valgt => valgt?.id != this.initieltValgtAlternativ()?.id))), {initialValue: false});
    }

    rediger() {
        RedigerAvstemningDialogKomponent.visRediger(this.matDialog, this.avstemning(), this.avstemning().eiendeGruppeId);
    }

    async slett() {
        await this.avhengigheter.slett(this.avstemning().id);
    }

    async nullstillAvgitteStemmer() {
        await this.avhengigheter.nullstillAvgittStemmer(this.avstemning().id);
        this.meldingTilBruker.infomelding(new I18nTekst((i18n("Stemmegivning på avstemning har blitt nullstilt"))));
    }

    async aapneForAvstemning() {
        await this.avhengigheter.åpneForAvstemning(this.avstemning().id);
        this.meldingTilBruker.infomelding(this.oversettelser.oversett(i18n("Det er nå åpnet for stemmegivning på avstemning")));
    }

    async stengeForAvstemning() {
        await this.avhengigheter.stengeForAvstemning(this.avstemning().id);
        this.meldingTilBruker.infomelding(this.oversettelser.oversett(i18n("Det er nå lukket for stemmegivning på avstemning")));
    }

    opprettBeslutning() {
        BeslutningOpprettDialogKomponent.visNyBeslutningBasertPåAvstemning(this.matDialog, this.avstemning(), this.avstemningsstatus(), this.oversettelser);
    }

    nullstillAvgittStemmeMedBekreftelse() {
        this.dialog.advarsel({
            tittel: this.oversettelser.oversett(i18n('Slett avgitt stemme')),
            innhold: this.oversettelser.oversett(i18n('Du kan frem til avstemningen lukkes endre din stemme')),
            okKnapp: this.oversettelser.oversett(i18n('Slett')),
            avbrytKnapp: this.oversettelser.oversett(i18n('Avbryt'))
        }).then(bekreftet => {
            if (bekreftet) this.nullstillAvgittStemme();
        });
    }

    async avgiStemme() {
        if (!this.brukerKanStemme()) return;
        await this.arbeider.utfør(() => this.avhengigheter.avgiStemme(this.avstemning().id, this.valgtAlternativ.value?.id ?? null));
        this.meldingTilBruker.infomelding(this.oversettelser.oversett(i18n('Din stemme er registrert')));
    }

    private async nullstillAvgittStemme() {
        await this.avhengigheter.avgiStemme(this.avstemning().id, null);
        this.meldingTilBruker.infomelding(this.oversettelser.oversett(i18n('Din stemme har blitt slettet')));
    }
}

@Injectable()
export class AvstemningListevisningKomponentAvhengigheter {

    constructor(private avstemningDatatjeneste: AvstemningDatatjeneste) {
    }

    async åpneForAvstemning(id: AvstemningId): Promise<void> {
        await this.avstemningDatatjeneste.åpneForAvstemning(id);
    }

    async stengeForAvstemning(id: AvstemningId): Promise<void> {
        await this.avstemningDatatjeneste.stengForAvstemning(id);
    }

    async avgiStemme(id: AvstemningId, avstemningsalternativId: AvstemningsalternativId | null): Promise<void> {
        await this.avstemningDatatjeneste.avgiStemme(id, avstemningsalternativId);
    }

    async nullstillAvgittStemmer(id: AvstemningId): Promise<void> {
        await this.avstemningDatatjeneste.nullstillAvgitteStemmerForAvstemning(id);
    }

    async slett(id: AvstemningId): Promise<void> {
        await this.avstemningDatatjeneste.slett(id);
    }

}
